"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { keyframes } from "tss-react";
import Icon from "../../icons/index.js";
import renderIcon from "../../icons/render-icon.js";
import makeStyles from "../../theming/make-styles.js";
const rotate = keyframes `
  100% {
    transform: rotate(360deg);
    stroke-dasharray: 0 calc(3.141px * var(--radius));
    stroke-dashoffset: 0;
  }
  50% {
    transform: rotate(0deg);
    stroke-dasharray: calc(3.141px * var(--radius)) 0;
    stroke-dashoffset: calc(3.141px * var(--radius));
  }
  0% {
    transform: rotate(0deg);
    stroke-dasharray: 0 calc(3.141px * var(--radius));
    stroke-dashoffset: calc(6.282px * var(--radius));
  }
`;
const useStyles = makeStyles()((theme, { animate, color, fill, gapStartDegree, height, indeterminate, pointerDegree, pointerRadius, radius, railColor, transparent, width }) => {
    const palette = theme.palette(color).circularProgress;
    return {
        icon: {
            transform: `scale(0.5) translate(-${theme.size(width / 2)}, -${theme.size(height / 2)})`,
            transformOrigin: "50%",
        },
        indeterminate: {
            animation: `${rotate} 1.5s infinite linear`,
        },
        pointer: Object.assign({ strokeWidth: 0, transform: indeterminate ? undefined : `rotate(${pointerDegree}deg) translate(0.5px, ${pointerRadius}px)`, transformOrigin: "50%" }, (animate && {
            transitionDuration: "0.2s",
            transitionProperty: "transform",
            transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        })),
        progress: Object.assign({ "--radius": radius, "stroke": palette.progress.strokeColor, "transform": indeterminate ? undefined : `rotate(${gapStartDegree}deg)`, "transformOrigin": "50%" }, (animate && {
            transitionDuration: "0.2s",
            transitionProperty: "stroke, stroke-dasharray, stroke-dashoffset",
            transitionTimingFunction: "cubic-bezier(0.0, 0.0, 1.0, 1.0)",
        })),
        rail: {
            fill: fill ? (fill === "rail" ? palette.rail.fillColor : theme.palette(fill).base[500]) : undefined,
            stroke: transparent ? "transparent" : railColor ? theme.palette(railColor).base[500] : palette.rail.strokeColor,
            transform: indeterminate ? undefined : `rotate(${gapStartDegree}deg)`,
            transformOrigin: "50%",
        },
    };
});
const GAP_POSITION_OFFSET = {
    top: 180,
    right: -90,
    bottom: 0,
    left: 90,
};
export const CircularProgress = React.forwardRef(function CircularProgress(_a, ref) {
    var { animate = true, color = "primary", fill, gapDegree = 0, gapPosition = gapDegree > 0 ? "bottom" : "top", height = 24, icon, indeterminate = true, pointer = false, railColor, railWidth = 3, strokeWidth: sW = 3, transparent = false, value = 0, width: w = 24 } = _a, props = __rest(_a, ["animate", "color", "fill", "gapDegree", "gapPosition", "height", "icon", "indeterminate", "pointer", "railColor", "railWidth", "strokeWidth", "transparent", "value", "width"]);
    const strokeWidth = typeof sW === "string" ? Number.parseInt(sW) : sW;
    const pointerRadius = railWidth > strokeWidth ? railWidth : strokeWidth;
    const radius = ((typeof w === "string" ? Number.parseInt(w) : w) - (railWidth > strokeWidth ? railWidth : strokeWidth)) / 2;
    const { classes, cx } = useStyles({
        animate,
        color,
        fill,
        gapStartDegree: 90 + gapDegree / 2 + (360 - gapDegree) / 360 + GAP_POSITION_OFFSET[gapPosition],
        height: Number(height),
        indeterminate,
        pointerDegree: ((360 - gapDegree) / 100) * value + GAP_POSITION_OFFSET[gapPosition] - 180 + gapDegree / 2,
        pointerRadius,
        radius,
        railColor,
        transparent,
        width: Number(w),
    });
    const circumference = 2 * Math.PI * radius;
    const strokeLength = circumference * ((360 - gapDegree) / 360);
    const width = 2 * radius + (railWidth > strokeWidth ? railWidth : strokeWidth) * 2;
    const svgProps = React.useMemo(() => {
        if (!indeterminate) {
            return Object.assign(Object.assign({}, props), { "aria-valuemax": 100, "aria-valuemin": 0, "aria-valuenow": value > 100 ? 100 : value });
        }
        return props;
    }, [indeterminate, props, value]);
    const railProps = React.useMemo(() => {
        return {
            cx: width / 2,
            cy: width / 2,
            strokeDasharray: indeterminate ? undefined : strokeLength,
            strokeWidth: railWidth,
        };
    }, [indeterminate, railWidth, strokeLength, width]);
    const progressProps = React.useMemo(() => {
        const dashOffset = indeterminate ? 0 : strokeLength * ((100 - value) / 100);
        return {
            cx: width / 2,
            cy: width / 2,
            strokeDasharray: `${strokeLength} ${circumference}`,
            strokeDashoffset: dashOffset,
            strokeWidth,
        };
    }, [circumference, indeterminate, strokeLength, strokeWidth, value, width]);
    return (_jsxs(Icon, Object.assign({ ref: ref, color: color, "data-component": "circular-progress", height: height, role: "progressbar", width: w }, svgProps, { viewBox: `0 0 ${width} ${width}`, children: [_jsx("circle", Object.assign({ className: classes.rail, "data-role": "rail", r: radius }, railProps)), (indeterminate || !pointer) && (_jsx("circle", Object.assign({ className: cx(classes.progress, indeterminate && classes.indeterminate), "data-role": "progress", r: radius, stroke: color }, progressProps))), !indeterminate && pointer && _jsx("circle", { className: classes.pointer, cx: "50%", cy: 0, fill: "currentColor", r: strokeWidth / 2 }), icon && renderIcon(icon, classes.icon, { height, width: w, x: "50%", y: "50%" })] })));
});
export default CircularProgress;
//# sourceMappingURL=circular-progress.js.map